import { css } from "@emotion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { useOrganisation } from "../lib/organisation";
import { Router } from "../App";
import { slugify } from "../lib/slugify";
import { Button } from "../components/button";
import { appContainer, formGroup, formLabel, formInput, formError } from "@/styles/shared";

const createMain = css`
  max-width: 480px;
  margin: 0 auto;
  padding: 80px 24px;
`;

const createTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-2xl);
  font-weight: 800;
  margin-bottom: 8px;
`;

const createDescription = css`
  opacity: 0.6;
  margin-bottom: 32px;
`;

const fullWidthBtn = css`
  width: 100%;
  margin-top: 16px;
`;

export function CreateOrganisationPage() {
  const { t } = useTranslation();
  const { addOrganisation } = useOrganisation();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t("organisation.nameRequired"));
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const organisation = await trpcClient.createOrganisation.mutate({ name: name.trim() });
      addOrganisation(organisation);
      Router.push("Homepage", { orgSlug: slugify(name.trim()) });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("organisation.createError"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div css={appContainer}>
      <main css={createMain}>
        <h2 css={createTitle}>{t("organisation.createTitle")}</h2>
        <p css={createDescription}>{t("organisation.createDescription")}</p>
        <form onSubmit={handleSubmit}>
          <div css={formGroup}>
            <label htmlFor="org-name" css={formLabel}>
              {t("organisation.nameLabel")}
            </label>
            <input
              id="org-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              css={formInput}
              placeholder={t("organisation.namePlaceholder")}
              required
              disabled={creating}
              autoFocus
            />
          </div>
          {error && <div css={formError}>{error}</div>}
          <Button type="submit" variant="primary" css={fullWidthBtn} disabled={creating}>
            {creating ? t("organisation.creating") : t("organisation.createButton")}
          </Button>
        </form>
      </main>
    </div>
  );
}
